import { Alert, Spin, theme, Layout, Breadcrumb, Button, Select, Card, Input, Modal, message, Pagination } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { uniqBy, debounce } from 'lodash';

import { getSharedContent, putSharedContent, deleteSharedContent } from '../api';
import useAuth from '../hooks/usePropelAuth';
import RichTextEditor from '../components/ContentEditor/sharedcontent';

const { Header } = Layout;

const SharedContentList: React.FC<{ data: any[] }> = ({ data }) => {
  const auth = useAuth();

  const [selectedProjectType, setSelectedProjectType] = React.useState<string | undefined>(undefined);
  const [selectedProjectTypeGroup, setSelectedProjectTypeGroup] = React.useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const pageSize = 10;

  const client = useQueryClient();

  const {
    mutate: updateSharedContent,
  } = useMutation(putSharedContent, {
    onSuccess: () => client.invalidateQueries('shared_content'),
    onError: (err: any) => {
      message.error('Unable to save content: ' + (err?.message || ''));
      client.invalidateQueries('shared_content');
    }
  });

  const { mutate: removeSharedContent } = useMutation(deleteSharedContent, {
    onSuccess: () => client.invalidateQueries('shared_content')
  });

  const onSharedContentUpdate = (item, key) => (value) => {
    updateSharedContent({
      sharedContentID: item.id,
      data: { [key || 'title']: value }
    });
  };

  const onDebouncedSharedContentTitleUpdate = (item, key) => debounce(onSharedContentTitleUpdate(item, key), 500);
  const onSharedContentTitleUpdate = (item, key) => (evt) => {
    updateSharedContent({
      sharedContentID: item.id,
      data: { [key || 'title']: evt.target.value }
    });
  };

  const projectTypes = useMemo(() => uniqBy(data, 'project_type').map(d => d.project_type).filter(Boolean), [data]);
  const projectTypeGroups = useMemo(() => uniqBy(data, 'project_type_group').map(d => d.project_type_group).filter(Boolean), [data]);

  const onProjectTypeChange = useCallback((value) => {
    setCurrentPage(1);
    setSelectedProjectType(value);
  }, []);

  const onProjectTypeGroupChange = useCallback((value) => {
    setCurrentPage(1);
    setSelectedProjectTypeGroup(value);
  }, []);

  const filteredData = useMemo(() => {
    return data.filter(d => {
      const matchesType = selectedProjectType ? d.project_type === selectedProjectType : true;
      const matchesGroup = selectedProjectTypeGroup ? d.project_type_group === selectedProjectTypeGroup : true;
      return matchesType && matchesGroup;
    });
  }, [data, selectedProjectType, selectedProjectTypeGroup]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const showDeleteConfirm = (item) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this shared content?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        removeSharedContent({ sharedContentID: item.id });
      }
    });
  };

  return (
    <div className='p-4'>
      <div className='flex items-center gap-3'>
        <Select
          className='w-56'
          placeholder='Select project type group'
          options={projectTypeGroups.map(p => ({ label: p, value: p }))}
          allowClear
          value={selectedProjectTypeGroup}
          onChange={onProjectTypeGroupChange}
        />
        <Select
          className='w-56'
          placeholder='Select project type'
          options={projectTypes.map(p => ({ label: p, value: p }))}
          allowClear
          value={selectedProjectType}
          onChange={onProjectTypeChange}
        />
      </div>

      <div className='h-full w-full flex flex-col gap-5 mt-5'>
        {paginatedData.map((d) => (
          <Card key={d.id}>
            <div className='mb-2 flex justify-between items-center'>
              <Input
                defaultValue={d.name}
                onChange={onDebouncedSharedContentTitleUpdate(d, 'name')}
              />
              <Button className='hidden' type='primary' danger onClick={() => showDeleteConfirm(d)}>
                Delete
              </Button>
            </div>
            <RichTextEditor
              data={d.content}
              readOnly={!auth?.roles?.some(role =>
                ['internal_tool:super_admin', 'internal_tool:team_lead'].includes(role)
              )}
              onDataChange={onSharedContentUpdate(d, 'content')}
            />
          </Card>
        ))}
      </div>

      <div className="flex justify-center py-6">
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredData.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};



const SharedContent: React.FC = () => {
    const {
        token: { colorBgContainer }
    } = theme.useToken();

    const { isLoading, isError, data } = useQuery('shared_content', getSharedContent);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[80vh] w-full">
              <Spin tip="Loading" size="large" />
            </div>
          );
    } else if (isError) {
        return (
            <Alert
                message="Something went wrong"
                description="Something went wrong during loading the shared content. Please wait for sometime, and refresh the page."
                type="error"
            />
        );
    }

    const sharedContent = data?.data?.data;

    return (
        <>
            <Header className='p-4 flex items-center justify-between' style={{ background: colorBgContainer }}>
                <div className='flex items-center gap-2'>
                    <Breadcrumb
                        className='mx-4'
                        items={[{
                            title: <Link to='/projects'>All projects</Link>
                        }, {
                            title: 'Shared Content'
                        }]}
                    />
                </div>
            </Header>
            <SharedContentList data={sharedContent} />
        </>
    );
};

export default SharedContent;
